import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { sanitizeUrl } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { nip19 } from 'nostr-tools';

interface ProfilePageProps {
  pubkey: string;
}

export function ProfilePage({ pubkey }: ProfilePageProps) {
  const { nostr } = useNostr();

  const { data: event, isLoading, error } = useQuery({
    queryKey: ['profile', pubkey],
    queryFn: async ({ signal }) => {
      const events = await nostr.query([{ kinds: [0], authors: [pubkey], limit: 1 }], { signal });
      return events[0] ?? null;
    },
    enabled: !!pubkey,
  });

  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="flex items-center space-x-4 mb-6">
          <Skeleton className="w-20 h-20 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-destructive">Profile not found.</p>
      </div>
    );
  }

  // Parse metadata safely
  let parsedMetadata: Record<string, unknown> = {};
  try {
    parsedMetadata = JSON.parse(event.content);
  } catch {
    // Keep empty object if parsing fails
  }

  const displayName = parsedMetadata.name ?? genUserName(pubkey);

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="flex items-center space-x-4 mb-6">
        {parsedMetadata.picture ? (
          <img src={sanitizeUrl(String(parsedMetadata.picture))} alt={String(parsedMetadata.name)} className="w-20 h-20 rounded-full" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-cyan-400" />
        )}
        <div>
          <h1 className="text-2xl font-bold">{displayName}</h1>
          {parsedMetadata.nip05 && (
            <p className="text-sm text-muted-foreground">{String(parsedMetadata.nip05)}</p>
          )}
        </div>
      </div>
      
      {parsedMetadata.about && (
        <Card className="p-4 mb-6">
          <p className="text-sm text-muted-foreground">{String(parsedMetadata.about)}</p>
        </Card>
      )}
    </div>
  );
}